import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { Season } from 'src/app/core/domain/season.enum';
import { Unsubscriber } from 'src/app/core/unsubscriber/unsubscriber';
import { ScreenSizeService } from 'src/app/core/utils/screen-size.service';
import { SisMediaAssetCategory } from 'src/app/webreport/domain/sismedia-asset-category.enum';
import { SisMediaItem } from 'src/app/webreport/domain/sismedia-item.model';
import { WebReport } from 'src/app/webreport/domain/webreport.model';
import { WebReportCategoryGroupItem } from 'src/app/webreport/domain/webreport-category-config.model';
import { WebReportConfig } from 'src/app/webreport/domain/webreport-config.model';
import { WebReportDesktop } from 'src/app/webreport/domain/webreport-desktop.model';
import { WebReportDesktopAssetGroup } from 'src/app/webreport/domain/webreport-desktop-asset-group.model';
import { WebReportDesktopRow } from 'src/app/webreport/domain/webreport-desktop-row.model';
import { WebReportInfotext } from 'src/app/webreport/domain/webreport-infotext.model';
import { WebReportMeteoInfoItem } from 'src/app/webreport/domain/webreport-meteo-info-item.model';
import { WebReportMobile } from 'src/app/webreport/domain/webreport-mobile.model';
import { WebReportMobileAssetCategory } from 'src/app/webreport/domain/webreport-mobile-asset-category.enum';
import { WebReportService } from 'src/app/webreport/webreport.service';
import { environment } from 'src/environments/environment';
import pixelWidth from 'string-pixel-width';
import { findIana } from 'windows-iana';

@Component({
  templateUrl: './webreport.page.html',
  styleUrls: ['./webreport.page.scss'],
  selector: 'sis-webreport',
  standalone: false,
})
export class WebReportPage extends Unsubscriber implements OnInit {
  private static readonly titleTypesByAssetCategoryMap: Map<SisMediaAssetCategory, Map<string, string>> = new Map([
    [
      SisMediaAssetCategory.Slope,
      new Map([
        ['pisteleicht', 'Slope'],
        ['pistemittel', 'Slope'],
        ['pisteschwer', 'Slope'],
        ['langlauf', 'Crosscountry'],
        ['langlaufklassisch', 'Crosscountry'],
        ['langlaufskating', 'Crosscountry'],
        ['schlitteln', 'Sledging'],
        ['airboard', 'Airboard'],
      ]),
    ],
    [
      SisMediaAssetCategory.Trail,
      new Map([
        ['wandern', 'Hiking'],
        ['schneeschuh', 'Snowshoe'],
        ['bike', 'Bike'],
        ['downhill', 'Bike'],
        ['skitour', 'Skitour'],
      ]),
    ],
    [
      SisMediaAssetCategory.Poi,
      new Map([
        ['shop', 'ShopRental'],
        ['schlittenvermietung', 'ShopRental'],
        ['skivermietung', 'ShopRental'],
        ['trottivermietung', 'ShopRental'],
        ['veloverleih', 'ShopRental'],
        ['vermietung', 'ShopRental'],
      ]),
    ],
  ]);

  private static readonly titleOrder = [
    SisMediaAssetCategory[SisMediaAssetCategory.Slope],
    'Slope',
    'Crosscountry',
    'Sledging',
    'Airboard',
    SisMediaAssetCategory[SisMediaAssetCategory.Trail],
    'Hiking',
    'Snowshoe',
    'HikingWinter',
    'Bike',
    'ShopRental',
    SisMediaAssetCategory[SisMediaAssetCategory.Poi],
  ];

  private readonly selectedAssetCategory$ = new ReplaySubject<WebReportMobileAssetCategory>(1);

  private readonly urlParams$: Observable<[Params, Params]> = combineLatest([
    this.activatedRoute.params,
    this.activatedRoute.queryParams,
  ]);

  readonly webReport$: Observable<WebReport> = this.urlParams$.pipe(
    switchMap(([params, queryParams]) => {
      const urlName = params['urlName'] as string;
      const seasonUrlParam: number = +queryParams['season'] as number;
      const columnsInParams: number = Number.parseInt(queryParams['columns'], 10);
      const assetFilters: string[] =
        (queryParams['hide'] as string)
          ?.trim()
          .toLowerCase()
          .split(',')
          .filter((f) => f !== '') ?? [];

      return this.webReportService.requestSisMediaAssets(urlName, columnsInParams).pipe(
        map((webReport) => {
          const assetCategories = [...webReport.sisMediaItemsByCategory.keys()];

          assetCategories.forEach((assetCategory) => {
            if (assetFilters.includes(SisMediaAssetCategory[assetCategory].toLowerCase())) {
              webReport.sisMediaItemsByCategory.delete(assetCategory);
            }
          });

          webReport.activeSeasonByTenantAlias = new Map<string, Season>();
          webReport.sisMediaSettingByTenantAlias.forEach((settings, tenantAlias) => {
            if (seasonUrlParam in Season) {
              webReport.activeSeasonByTenantAlias.set(tenantAlias, seasonUrlParam);
              return;
            }

            const now = DateTime.now();
            const isWinterSeason =
              settings == null ||
              (now >= DateTime.fromJSDate(settings.winterSeasonStartDate) &&
                now <= DateTime.fromJSDate(settings.winterSeasonEndDate));

            const season = isWinterSeason ? Season.Winter : Season.Summer;
            webReport.activeSeasonByTenantAlias.set(tenantAlias, season);
          });

          webReport.sisMediaItemsByCategory.forEach((items, category) => {
            const filteredItems = items.filter((item) => {
              if (
                seasonUrlParam in Season &&
                seasonUrlParam !== Season.SummerAndWinter &&
                item.season !== seasonUrlParam &&
                item.season !== Season.SummerAndWinter
              ) {
                return false;
              }

              return !assetFilters.some((assetFilter) => item.type?.toLowerCase() === assetFilter);
            });

            if (filteredItems.length === 0) {
              webReport.sisMediaItemsByCategory.delete(category);
            } else {
              webReport.sisMediaItemsByCategory.set(category, filteredItems);
            }
          });

          if (assetFilters.includes('meteo')) {
            webReport.meteoInfosByTenantAlias = new Map<string, WebReportMeteoInfoItem[]>();
          }

          return webReport;
        }),
      );
    }),
    shareReplay(1),
  );

  readonly webReportMeteoItems$: Observable<Map<string, WebReportMeteoInfoItem[]>> = this.webReport$.pipe(
    map((webReport) => {
      const webReportMeteoItems = new Map<string, WebReportMeteoInfoItem[]>();
      if (!webReport) {
        return webReportMeteoItems;
      }

      webReport.meteoInfosByTenantAlias.forEach((values, key) => {
        const filteredValues = values
          .filter((value) => webReport.exportConfig?.meteoStations?.includes(value.location))
          .sort((a, b) => {
            const indexA = webReport.exportConfig?.meteoStations?.indexOf(a.location);
            const indexB = webReport.exportConfig?.meteoStations?.indexOf(b.location);
            return indexA - indexB;
          });

        if (filteredValues.length > 0) {
          webReportMeteoItems.set(key, filteredValues);
        }
      });

      return webReportMeteoItems;
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );

  showHeader = false;
  showFooter = false;
  showLegend = false;
  isMobileDataLoaded = false;
  isDesktopDataLoaded = false;
  allowPageBreak = false;
  showTimestamp = false;

  bigScreenMode: boolean;
  config: WebReportConfig;
  activeSeasonByTenantAlias: Map<string, Season>;

  // big screen data
  alias: string;
  headerImage: string;
  websiteDisplayUrl: string | undefined;
  websiteUrl: string | undefined;
  phone: string;
  timestamp: Date;

  webReportDesktopItems: WebReportDesktop[] = [];
  gridMinHeight = 0;

  // small screen data
  titleTranslationString: string;
  selectedAssetCategory: string;
  webReportItemsByCategory: Map<SisMediaAssetCategory, SisMediaItem[]> = new Map();
  hasLifts: boolean;
  hasSlopes: boolean;
  hasGastros: boolean;
  hasTrails: boolean;
  hasPois: boolean;
  showMeteo: boolean;
  mobileStyleTenant = false;

  @ViewChild('desktopGrid', { read: ElementRef }) desktopGrid: ElementRef;

  constructor(
    private webReportService: WebReportService,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private screenSizeService: ScreenSizeService,
    private title: Title,
  ) {
    super();
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams
      .pipe(
        switchMap((queryParams) => {
          if (queryParams['columns']) {
            return of(true);
          } else {
            return this.screenSizeService.bigScreenMode$;
          }
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe((bigScreenMode) => (this.bigScreenMode = bigScreenMode));

    this.urlParams$.pipe(takeUntil(this.onDestroy$)).subscribe(([, queryParams]) => {
      this.showHeader = queryParams['header'] === 'true';
      this.showFooter = queryParams['footer'] === 'true';
      this.showLegend = queryParams['legend'] === 'true';
      this.allowPageBreak = queryParams['allowPageBreak'] === 'true';
      this.showTimestamp = queryParams['timestamp'] === 'true';
    });

    this.webReport$.pipe(takeUntil(this.onDestroy$)).subscribe((webReport) => {
      this.setHeaderImageUrl(webReport.exportConfig.headerImage);

      this.alias = webReport.exportConfig.tenantAlias ?? webReport.tenant.alias;
      this.websiteUrl = webReport.exportConfig.tenantWebsite ?? webReport.tenant.website;
      this.websiteDisplayUrl = this.websiteUrl?.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/$/, '');
      this.phone = webReport.exportConfig.tenantPhoneNumber ?? webReport.tenant.phone;
      this.config = webReport.exportConfig;
      this.activeSeasonByTenantAlias = webReport.activeSeasonByTenantAlias;

      this.hasLifts = webReport.sisMediaItemsByCategory.has(SisMediaAssetCategory.Lift);
      this.hasSlopes = webReport.sisMediaItemsByCategory.has(SisMediaAssetCategory.Slope);
      this.hasGastros = webReport.sisMediaItemsByCategory.has(SisMediaAssetCategory.Gastro);
      this.hasTrails = webReport.sisMediaItemsByCategory.has(SisMediaAssetCategory.Trail);
      this.hasPois = webReport.sisMediaItemsByCategory.has(SisMediaAssetCategory.Poi);
      this.showMeteo = webReport.meteoInfosByTenantAlias.size > 0 && webReport.exportConfig?.meteoStations?.length > 0;
      this.mobileStyleTenant = webReport.exportConfig?.mobileStyleTenant || false;

      this.webReportItemsByCategory = webReport.sisMediaItemsByCategory;
      this.isMobileDataLoaded = true;

      if (!this.selectedAssetCategory) {
        const initialCategory = this.hasLifts
          ? WebReportMobileAssetCategory.Lift
          : this.hasSlopes
            ? WebReportMobileAssetCategory.Slope
            : this.hasGastros
              ? WebReportMobileAssetCategory.Gastro
              : this.hasTrails
                ? WebReportMobileAssetCategory.Trail
                : this.hasPois
                  ? WebReportMobileAssetCategory.Poi
                  : WebReportMobileAssetCategory.Meteo;

        this.selectedAssetCategory$.next(initialCategory);
      }

      const timeZoneIANA = findIana(webReport.tenant.timeZoneId)[0];
      this.timestamp = DateTime.now().setZone(timeZoneIANA).toJSDate();

      if (this.desktopGrid) {
        const currentHeight = this.desktopGrid.nativeElement?.offsetHeight;
        this.gridMinHeight = Math.max(this.gridMinHeight, currentHeight);
      }

      this.webReportDesktopItems = this.buildWebReportDesktopItems(webReport).sort((a, b) => a.order - b.order);

      this.isDesktopDataLoaded = true;

      this.title.setTitle(`${environment.titlePrefix}Webreport - ${this.alias}`);
    });

    this.selectedAssetCategory$.pipe(takeUntil(this.onDestroy$)).subscribe((selectedAssetCategory) => {
      this.selectedAssetCategory = WebReportMobileAssetCategory[selectedAssetCategory];
      this.titleTranslationString = this.selectedAssetCategory
        ? `webreport.assetCategory${this.selectedAssetCategory}`
        : '';
    });

    window.onmessage = (event: MessageEvent): void => {
      if (event?.data?.update) {
        this.webReportService.requestUpdate();
      }
    };
  }

  assetCategorySelected(selection: keyof typeof WebReportMobileAssetCategory): void {
    const assetCategory = WebReportMobileAssetCategory[selection];
    this.selectedAssetCategory$.next(assetCategory);
  }

  trackByTenantAlias(_index: number, item: WebReportDesktop | WebReportMobile): string {
    return item.tenantAlias;
  }

  trackByGuid(_index: number, item: SisMediaItem | undefined | null): string {
    if (item == null) {
      return _index.toString();
    }
    return item?.guid;
  }

  private toAssetEnum(name: string): SisMediaAssetCategory | undefined {
    return SisMediaAssetCategory[name as keyof typeof SisMediaAssetCategory];
  }

  private sisMediaItemsByTenantAlias(items: SisMediaItem[]): Map<string, SisMediaItem[]> {
    if (!items?.length) {
      return new Map<string, SisMediaItem[]>();
    }

    return items.reduce((p, c) => {
      let list = p.get(c.tenantAlias);
      if (!list) {
        list = [];
        p.set(c.tenantAlias, list);
      }
      list.push(c);
      return p;
    }, new Map<string, SisMediaItem[]>());
  }

  private buildWebReportDesktopItems(webReport: WebReport): WebReportDesktop[] {
    const assetMap = new Map<SisMediaAssetCategory, Map<string, SisMediaItem[]>>([
      [
        SisMediaAssetCategory.Lift,
        this.sisMediaItemsByTenantAlias(webReport.sisMediaItemsByCategory.get(SisMediaAssetCategory.Lift)),
      ],
      [
        SisMediaAssetCategory.Slope,
        this.sisMediaItemsByTenantAlias(webReport.sisMediaItemsByCategory.get(SisMediaAssetCategory.Slope)),
      ],
      [
        SisMediaAssetCategory.Trail,
        this.sisMediaItemsByTenantAlias(webReport.sisMediaItemsByCategory.get(SisMediaAssetCategory.Trail)),
      ],
      [
        SisMediaAssetCategory.Gastro,
        this.sisMediaItemsByTenantAlias(webReport.sisMediaItemsByCategory.get(SisMediaAssetCategory.Gastro)),
      ],
      [
        SisMediaAssetCategory.Poi,
        this.sisMediaItemsByTenantAlias(webReport.sisMediaItemsByCategory.get(SisMediaAssetCategory.Poi)),
      ],
    ]);

    const tenantAliases = new Set<string>();
    for (const [, tenantItemMap] of assetMap) {
      for (const tenantAlias of tenantItemMap.keys()) {
        tenantAliases.add(tenantAlias);
      }
    }

    const resultMap = new Map<
      string,
      {
        infotext: string;
        asset: Map<SisMediaAssetCategory, WebReportDesktopAssetGroup[]>;
      }
    >();

    for (const tenantAlias of tenantAliases) {
      if (!resultMap.has(tenantAlias)) {
        const map = new Map<SisMediaAssetCategory, WebReportDesktopAssetGroup[]>();
        for (const category of assetMap.keys()) {
          map.set(category, []);
        }
        resultMap.set(tenantAlias, {
          infotext: this.infotextLang(
            this.translateService.currentLang ?? this.translateService.defaultLang,
            webReport.infotextsByTenantAlias.get(tenantAlias),
          ),
          asset: map,
        });
      }

      const season = webReport.activeSeasonByTenantAlias.get(tenantAlias) ?? Season.SummerAndWinter;
      const columns = webReport.exportConfig.columns ?? 3;

      const itemsByCat = new Map<SisMediaAssetCategory, SisMediaItem[]>();
      for (const [cat, tenantItemMap] of assetMap) {
        itemsByCat.set(cat, tenantItemMap.get(tenantAlias) ?? []);
      }

      const allTenantItems = ([] as SisMediaItem[]).concat(...Array.from(itemsByCat.values()));
      const used = new Set<SisMediaItem>();
      const configGroups = this.config?.categories ?? [];
      for (const groupConfig of configGroups) {
        const entries: WebReportCategoryGroupItem[] = groupConfig?.group ?? [];
        if (!entries.length) {
          continue;
        }

        const allTypesLower = entries
          .reduce((acc: string[], e) => {
            if (e.types && e.types.length) acc.push(...e.types);
            return acc;
          }, [])
          .map((t) => t?.toLowerCase())
          .filter(Boolean);

        const matched = allTenantItems.filter((it) => it?.type && allTypesLower.includes(it.type.toLowerCase()));
        if (matched.length === 0) {
          continue;
        }

        const targetName = groupConfig.targetSisMediaCategory;
        const targetEnum = this.toAssetEnum(targetName) ?? SisMediaAssetCategory.Slope;

        const titles = this.getCategoryTitles(matched, SisMediaAssetCategory[targetEnum], season, undefined);

        const group: WebReportDesktopAssetGroup = { titles, items: this.buildRows(matched, columns) };
        resultMap.get(tenantAlias).asset.get(targetEnum).push(group);

        matched.forEach((m) => used.add(m));
      }

      for (const [cat, catItems] of itemsByCat) {
        const remaining = catItems.filter((it) => !used.has(it));
        if (remaining.length === 0) {
          continue;
        }

        const extraTitleTypes = WebReportPage.titleTypesByAssetCategoryMap.get(cat);
        const titles = this.getCategoryTitles(remaining, SisMediaAssetCategory[cat], season, extraTitleTypes);
        const group: WebReportDesktopAssetGroup = { titles, items: this.buildRows(remaining, columns) };
        resultMap.get(tenantAlias).asset.get(cat).push(group);
      }
    }

    const result: WebReportDesktop[] = [];
    for (const [tenantAlias, itemMap] of resultMap) {
      let order = webReport.tenantOrders?.indexOf(tenantAlias);
      if (order == null || order === -1) {
        order = 999;
      }

      let displayedTenantAlias = tenantAlias;
      const tenantConfig = webReport.exportConfig.tenantNames?.find((t) => t.tenantAlias == tenantAlias);
      if (tenantConfig) {
        displayedTenantAlias = tenantConfig.displayedTenantName;
      }

      result.push({
        tenantAlias,
        displayedTenantAlias,
        order,
        infotext: itemMap.infotext,
        liftsGroups: itemMap.asset.get(SisMediaAssetCategory.Lift) ?? [],
        slopesGroups: itemMap.asset.get(SisMediaAssetCategory.Slope) ?? [],
        trailsGroups: itemMap.asset.get(SisMediaAssetCategory.Trail) ?? [],
        gastrosGroups: itemMap.asset.get(SisMediaAssetCategory.Gastro) ?? [],
        poisGroups: itemMap.asset.get(SisMediaAssetCategory.Poi) ?? [],
      });
    }

    return result.sort((a, b) => a.order - b.order);
  }

  private setHeaderImageUrl(image: string): void {
    this.headerImage = `${environment.baseUrlPublicAssets}/webreport/header/${image}`;
  }

  private infotextLang(languageKey: string, infotext: WebReportInfotext): string {
    if (!infotext) {
      return null;
    }
    switch (languageKey) {
      case 'de':
        return infotext.textDe;
      case 'en':
        return infotext.textEn;
      case 'fr':
        return infotext.textFr;
      case 'it':
        return infotext.textIt;
      default:
        return infotext.textEn;
    }
  }

  private buildRows(items: SisMediaItem[], columns: number): WebReportDesktopRow[] {
    if (!items || items.length === 0) {
      return [];
    }

    const rowsCount = Math.ceil(items.length / columns);
    const rows: WebReportDesktopRow[] = new Array(rowsCount);
    for (let r = 0; r < rowsCount; r++) {
      rows[r] = {
        rowItems: new Array(columns),
        longestLabelLengths: new Array(columns),
      };
    }

    for (let i = 0; i < items.length; i++) {
      rows[i % rowsCount].rowItems[Math.floor(i / rowsCount)] = items[i];
    }

    const longestLabelArrayPixel = new Array(columns).fill(0);
    for (let i = 0; i < columns; i++) {
      for (const row of rows) {
        if (row.rowItems[i]) {
          longestLabelArrayPixel[i] = Math.max(
            longestLabelArrayPixel[i],
            pixelWidth(row.rowItems[i].label, { font: 'arial', size: 15 }),
          );
        }
      }
    }
    for (let r = 0; r < rowsCount; r++) {
      rows[r].longestLabelLengths = longestLabelArrayPixel;
    }

    return rows;
  }

  private getCategoryFromConfig(type: string): string | null {
    const configGroups = this.config?.categories ?? [];
    if (!type || configGroups.length === 0) {
      return null;
    }

    for (const groupConfig of configGroups) {
      for (const entry of groupConfig.group) {
        if (entry.types.some((t) => t?.toLowerCase() === type.toLowerCase())) {
          return entry.category;
        }
      }
    }
    return null;
  }

  private getCategoryTitles(
    items: SisMediaItem[],
    defaultTitle: string,
    season: Season,
    extraTitleTypes?: Map<string, string>,
  ): string[] {
    const titleTypes = extraTitleTypes ?? new Map<string, string>();

    return Array.from(
      new Set(
        items
          .map((item) => {
            const titleType = titleTypes.get(item.type);

            switch (titleType) {
              case 'Hiking':
                if (
                  (season === Season.Winter || season === Season.SummerAndWinter) &&
                  (item.season === Season.Winter || item.season === Season.SummerAndWinter)
                ) {
                  return `${titleType}Winter`;
                }
                if (
                  (season === Season.Summer || season === Season.SummerAndWinter) &&
                  (item.season === Season.Summer || item.season === Season.SummerAndWinter)
                ) {
                  return defaultTitle;
                }
                return null;
            }

            const configCategory = this.getCategoryFromConfig(item.type);
            if (configCategory) {
              return configCategory;
            }

            return titleType ?? defaultTitle;
          })
          .filter((a) => a)
          .sort((a, b) => WebReportPage.titleOrder.indexOf(a) - WebReportPage.titleOrder.indexOf(b)),
      ),
    ).map((t) => `webreport.assetCategory${t}`);
  }
}




